import * as React from "react";
import { useEffect, useState } from "react";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import {
  GroupsService,
  GroupsServiceProps,
} from "../../../../application/service/smart-apply/groups/groups-service";
import { useModal } from "../../../../utils/custom-hooks";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { Loader } from "../../../organisms/common/loading";
import { GroupNameModal } from "../../../organisms/common/group-name-modal";
import { Link, Redirect, useLocation } from "react-router-dom";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { SettingR } from "../route";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { useAuthContextV2 } from "../../../../auth-state";
import { useFeatureFlag_IgnoreAclSettingByGroup } from "../../../../feature-flags";
import {
  InformationBanner,
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from "@graffer-inc/console-design-kit";
import { BatchImportGroupModal } from "../../../organisms/common/batch-import-group-modal";
import { Dropdown } from "../../../molecules/dropdown";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import { Pagination } from "@graffer-inc/console-design-kit";
import { matchType } from "../../../../application/domain/base/base-type";

export const GroupsPage: React.FC<GroupsServiceProps> = ({
  isLoading,
  isBatchImporting,
  groups,
  organizationId,
  orgSlug,
  paging,
  query,
  setPage,
  movePage,
  setQuery,
  fetchOrganization,
  fetchGroups,
  searchGroups,
  onCreateGroup,
  onUpdateGroup,
  onDownloadCsv,
  importByCsv,
  updateByCsv,
  downloadTemplateCsv,
}) => {
  const { auth } = useAuthContextV2();
  const { page, name } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
  };

  const groupNameModal = useModal(false);
  const csvImportModal = useModal(false);
  const csvUpdateModal = useModal(false);
  const dropdownModal = useModal(false);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [isNewGroup, setIsNewGroup] = useState(false);
  const orgId = auth.data?.orgId;
  useEffect(() => {
    if (typeof orgId === "string") {
      fetchOrganization(orgId);
    }
    setPage(parseInt(page!, 10) || 1);
    fetchGroups(organizationId);
  }, [
    setPage,
    page,
    name,
    organizationId,
    fetchGroups,
    orgId,
    fetchOrganization,
  ]);

  // group managerにグループ作成・編集権限が付与された場合でも、IP制限の有効/無効切り替えはAdmin userに限定する。
  const canIgnoreAclSetting =
    useFeatureFlag_IgnoreAclSettingByGroup(orgSlug) && auth.hasRole("Admin");

  if (!auth.hasPermissions("groups.read")) {
    return <Redirect to={SettingR.MyAccount.link({})} />;
  }

  if (!groups) {
    return null;
  }

  return (
    <SettingsTemplate breadcrumbs={[{ to: "/groups", title: "グループ一覧" }]}>
      <h1 className="title">
        <i className="fa fa-user-friends" /> グループ一覧
      </h1>
      <div>
        <nav className="level">
          <div className="level-left">
            <div className="level-item">
              <div className="field has-addons">
                <p
                  className="control has-icons-left has-icons-right"
                  style={{ minWidth: "280px" }}
                >
                  <input
                    className="input"
                    placeholder="グループ名で検索"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{
                      fontSize: "14px",
                    }}
                  />
                  <span className="icon is-small is-left">
                    <i className="fas fa-search" />
                  </span>
                  <span
                    className="icon is-small is-right"
                    role="button"
                    onClick={() => {
                      setQuery("");
                      searchGroups();
                    }}
                    style={{ pointerEvents: "initial", cursor: "pointer" }}
                  >
                    <i className="fas fa-times" />
                  </span>
                </p>
                <p className="control">
                  <SecondaryButton text={"検索"} onClick={searchGroups} />
                </p>
              </div>
            </div>
          </div>
          <div className="level-right">
            <RBACHidden requirePermissions={"groups.read"}>
              <div className="level-item">
                <SecondaryButton
                  text={"CSV出力"}
                  onClick={() => onDownloadCsv(orgId!)}
                />
              </div>
            </RBACHidden>
            <div className="level-item">
              <OutsideClickHandler onOutsideClick={dropdownModal.close}>
                <Dropdown
                  title="一括操作"
                  isActive={dropdownModal.isActive}
                  onClick={dropdownModal.open}
                  menus={[
                    {
                      title: "一括登録",
                      onClick: csvImportModal.open,
                      disable: !auth.hasPermissions("groups.create"),
                    },
                    {
                      title: "一括更新",
                      onClick: csvUpdateModal.open,
                      disable: !auth.hasPermissions("groups.update"),
                    },
                  ]}
                />
              </OutsideClickHandler>
            </div>
            <RBACHidden requirePermissions={"groups.create"}>
              <div className="level-item">
                <PrimaryButton
                  text={"新規登録"}
                  onClick={() => {
                    setIsNewGroup(true);
                    groupNameModal.open();
                  }}
                />
              </div>
            </RBACHidden>
          </div>
        </nav>
        <Loader isLoading={isLoading}>
          <table className="table is-fullwidth">
            <thead>
              <tr>
                <th>グループID</th>
                <th>グループ名</th>
                {canIgnoreAclSetting && <th>IPアドレス制御</th>}
                <th aria-label="ユーザー一覧" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="c-tr-has-tag">
                  <td>{convertToFormattedId(group.id!)}</td>
                  <td>
                    <div className="group-list-group-name-wrapper">
                      <span className="group-list-group-name">
                        {group.name}
                      </span>
                      <TextButton
                        icon="EditIcon"
                        text="編集"
                        onClick={() => {
                          setSelectedGroupId(group.id!);
                          setIsNewGroup(false);
                          groupNameModal.open();
                        }}
                      />
                    </div>
                  </td>
                  {canIgnoreAclSetting && (
                    <td>{group.isIgnoreAclSetting ? "無効" : "有効"}</td>
                  )}
                  <td>
                    <RBACHidden requirePermissions="groups.read">
                      <Link to={SettingR.GroupUsers.link({ id: group.id! })}>
                        ユーザー一覧
                      </Link>
                    </RBACHidden>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {groups.length === 0 &&
            matchType(!query ? "no_registered" : "no_results", {
              no_registered: (
                <div className="py-2">
                  <InformationBanner
                    message="グループがまだ登録されていません。"
                    description="「新規登録」より、グループを登録してください。"
                  />
                </div>
              ),
              no_results: (
                <div className="py-2">
                  <InformationBanner
                    message="条件に該当するグループが見つかりませんでした。"
                    description="検索条件を確認してください。"
                  />
                </div>
              ),
            })}

          <div style={{ marginTop: "16px" }}>
            <Pagination
              onPageClick={movePage}
              currentPage={paging.current}
              lastPage={paging.total}
            />
          </div>
        </Loader>
        {groupNameModal.isActive && (
          <GroupNameModal
            isLoading={isLoading}
            title={isNewGroup ? "グループを作成する" : "グループ名を更新する"}
            isActive={groupNameModal.isActive}
            onModal={groupNameModal.close}
            onSubmit={isNewGroup ? onCreateGroup : onUpdateGroup}
            targetGroup={
              isNewGroup ? null : groups.find((g) => g.id === selectedGroupId)!
            }
            buttonText={isNewGroup ? "作成する" : "更新する"}
            canIgnoreAclSetting={canIgnoreAclSetting}
          />
        )}
        {orgId && csvImportModal.isActive && (
          <BatchImportGroupModal
            orgId={orgId}
            isBatchImporting={isBatchImporting}
            isActive={csvImportModal.isActive}
            onClose={csvImportModal.close}
            onImport={importByCsv}
            onDownloadTemplateCsv={downloadTemplateCsv}
          />
        )}
        {orgId && csvUpdateModal.isActive && (
          <BatchImportGroupModal
            orgId={orgId}
            isBatchImporting={isBatchImporting}
            isActive={csvUpdateModal.isActive}
            batchType="UPDATE"
            onClose={csvUpdateModal.close}
            onImport={updateByCsv}
          />
        )}
      </div>
    </SettingsTemplate>
  );
};

export const GroupsPageContainer = connectPageService(
  GroupsPage,
  () => new GroupsService(),
);
